import { useState } from 'react'

import { api } from '../api/client'
import type { EagleCI, EagleParam } from '../api/types'
import { t } from '../i18n/lingua'
import { ErrorBanner } from './common'

/** Esempi di nomi: nomi di programmi, uguali in ogni lingua. */
const ESEMPIO_PROCESSO = 'sqlservr.exe'
const ESEMPIO_MODELLO = 'SQL*'

/**
 * Parametri definiti a mano (ADR-0022 §8): un **gruppo di servizi** — i
 * servizi scelti dall'inventario e/o un modello come `SQL*`, tutti attivi o
 * almeno N — oppure un **processo** per nome. Li legge l'agente a bordo.
 */
export function EagleNuovo({
  ciId, params, onDati,
}: { ciId: string; params: EagleParam[]; onDati: (d: EagleCI) => void }) {
  const [aperto, setAperto] = useState<'gruppo' | 'processo' | null>(null)
  const [nome, setNome] = useState('')
  const [modello, setModello] = useState('')
  const [scelti, setScelti] = useState<string[]>([])
  const [filtro, setFiltro] = useState('')
  const [regola, setRegola] = useState<'tutti' | 'almeno'>('tutti')
  const [minimo, setMinimo] = useState('1')
  const [processo, setProcesso] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [invio, setInvio] = useState(false)

  const servizi = params
    .filter((p) => p.kind === 'service')
    .map((p) => ({ nome: p.extra.service ?? p.name, etichetta: p.name }))
  const visibili = servizi.filter((s) =>
    !filtro || `${s.nome} ${s.etichetta}`.toLowerCase().includes(filtro.toLowerCase()))

  function chiudi() {
    setAperto(null)
    setNome('')
    setModello('')
    setScelti([])
    setFiltro('')
    setRegola('tutti')
    setMinimo('1')
    setProcesso('')
    setErrore(null)
  }

  async function salva() {
    setInvio(true)
    setErrore(null)
    try {
      const dati = aperto === 'gruppo'
        ? await api.ciEagleNuovo(ciId, {
            type: 'service_group', name: nome, services: scelti, pattern: modello,
            min: regola === 'almeno' ? Number(minimo) : null,
          })
        : await api.ciEagleNuovo(ciId, { type: 'process', process: processo })
      onDati(dati)
      chiudi()
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }

  if (!aperto) {
    return (
      <p style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="secondary" onClick={() => setAperto('gruppo')}>{t('Aggiungi un gruppo di servizi')}</button>
        <button type="button" className="secondary" onClick={() => setAperto('processo')}>{t('Aggiungi un processo')}</button>
      </p>
    )
  }
  return (
    <div className="eagle-punti">
      <ErrorBanner error={errore} />
      {aperto === 'gruppo' ? (
        <>
          <h4 style={{ marginTop: 0 }}>{t('Nuovo gruppo di servizi')}</h4>
          <label className="field" style={{ maxWidth: 360 }}>
            <span className="field__label">{t('Nome del gruppo')}</span>
            <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder={t('es. SQL Server')} />
          </label>
          <label className="field" style={{ maxWidth: 360 }}>
            <span className="field__label">{t('Modello (facoltativo)')}</span>
            <input value={modello} onChange={(e) => setModello(e.target.value)} placeholder={ESEMPIO_MODELLO} className="mono" />
            <span className="muted eagle-piccolo">{t('Comprende ogni servizio il cui nome corrisponde, anche quelli installati dopo.')}</span>
          </label>
          {servizi.length > 0 && (
            <fieldset className="field">
              <legend className="field__label">{t('Servizi scelti ({n})', { n: scelti.length })}</legend>
              <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder={t('Cerca un servizio')}
                     aria-label={t('Cerca un servizio')} style={{ maxWidth: 360 }} />
              <div className="eagle-scelta-servizi">
                {visibili.map((s) => (
                  <label key={s.nome}>
                    <input type="checkbox" checked={scelti.includes(s.nome)}
                           onChange={() => setScelti(scelti.includes(s.nome) ? scelti.filter((x) => x !== s.nome) : [...scelti, s.nome])} />
                    {' '}{s.etichetta}{s.etichetta !== s.nome && <span className="muted mono"> · {s.nome}</span>}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <label className="field" style={{ maxWidth: 360 }}>
            <span className="field__label">{t('Quando è a posto')}</span>
            <select value={regola} onChange={(e) => setRegola(e.target.value as 'tutti' | 'almeno')}>
              <option value="tutti">{t('Tutti i servizi del gruppo attivi')}</option>
              <option value="almeno">{t('Almeno un certo numero attivi')}</option>
            </select>
          </label>
          {regola === 'almeno' && (
            <label className="field" style={{ maxWidth: 160 }}>
              <span className="field__label">{t('Almeno')}</span>
              <input type="number" min={1} value={minimo} onChange={(e) => setMinimo(e.target.value)} />
            </label>
          )}
        </>
      ) : (
        <>
          <h4 style={{ marginTop: 0 }}>{t('Nuovo processo')}</h4>
          <label className="field" style={{ maxWidth: 360 }}>
            <span className="field__label">{t('Nome del processo')}</span>
            <input value={processo} onChange={(e) => setProcesso(e.target.value)} placeholder={ESEMPIO_PROCESSO} className="mono" />
            <span className="muted eagle-piccolo">{t('Giù se non ce n’è nessuno in esecuzione; il valore è quante istanze girano.')}</span>
          </label>
        </>
      )}
      <p style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 0 }}>
        <button type="button" onClick={salva}
                disabled={invio || (aperto === 'gruppo' ? !nome.trim() || (!modello.trim() && scelti.length === 0) : !processo.trim())}>
          {invio ? t('Salvo…') : t('Aggiungi')}
        </button>
        <button type="button" className="secondary" onClick={chiudi} disabled={invio}>{t('Annulla')}</button>
      </p>
    </div>
  )
}
